// Assistant prose. LLM output regularly contains markdown — bold, lists,
// links, fenced code — so we render through react-markdown with GFM. The
// component overrides keep typography aligned with Figma (14px body,
// secondary-900 text, 1.5 line-height) and don't introduce any spacing
// the bubble doesn't expect.

import {
  Box,
  Code,
  Heading,
  Link,
  ListItem,
  OrderedList,
  UnorderedList,
} from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import type { TextBlockProps } from './TextBlock.types';

const components: Components = {
  p: ({ children }) => (
    <Text
      variant="p"
      className="text-secondary-900 leading-[1.5] mb-2 last:mb-0 whitespace-pre-wrap"
    >
      {children}
    </Text>
  ),
  strong: ({ children }) => (
    <Text as="strong" variant="span" className="font-[550] text-secondary-900">
      {children}
    </Text>
  ),
  em: ({ children }) => (
    <Text as="em" variant="span" className="italic text-secondary-900">
      {children}
    </Text>
  ),
  a: ({ href, children }) => (
    <Link
      href={href && href.length > 0 ? href : '#'}
      isExternal
      color="primary-500"
      textDecoration="underline"
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </Link>
  ),
  ul: ({ children }) => (
    <UnorderedList pl={4} mb={2} spacing={1} className="text-secondary-900">
      {children}
    </UnorderedList>
  ),
  ol: ({ children }) => (
    <OrderedList pl={4} mb={2} spacing={1} className="text-secondary-900">
      {children}
    </OrderedList>
  ),
  li: ({ children }) => (
    <ListItem className="text-secondary-900 leading-[1.5]">{children}</ListItem>
  ),
  h1: ({ children }) => (
    <Heading as="h1" size="md" mt={2} mb={2} className="text-secondary-900">
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading as="h2" size="sm" mt={2} mb={2} className="text-secondary-900">
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading as="h3" size="xs" mt={2} mb={1} className="text-secondary-900">
      {children}
    </Heading>
  ),
  // Inline `code` vs fenced ```code``` — react-markdown passes the same node
  // type for both; the `pre` override below wraps fenced blocks. Here we
  // style the inline variant only.
  code: ({ children }) => (
    <Code
      bg="secondary-50"
      color="secondary-900"
      px="4px"
      py="2px"
      borderRadius="4px"
      fontSize="0.875em"
    >
      {children}
    </Code>
  ),
  pre: ({ children }) => (
    <Box
      as="pre"
      bg="secondary-50"
      borderRadius="6px"
      px="12px"
      py="10px"
      my="8px"
      overflowX="auto"
      fontSize="13px"
      lineHeight="1.45"
      className="text-secondary-900 font-mono whitespace-pre"
    >
      {children}
    </Box>
  ),
  blockquote: ({ children }) => (
    <Box
      as="blockquote"
      borderLeft="3px solid"
      borderColor="secondary-200"
      pl="12px"
      my="8px"
      className="text-secondary-700"
    >
      {children}
    </Box>
  ),
};

export const TextBlock = ({ data }: TextBlockProps) => {
  const source = data.content ?? '';
  if (!source) return null;
  return (
    <Box className="text-[14px] text-secondary-900">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={components}
      >
        {source}
      </ReactMarkdown>
    </Box>
  );
};
